import {
    type ArrayField,
    createArrayField,
    createObjectField,
    createPrimitiveField,
    createUnionField,
    type InferFieldValue,
    literal,
    type ValidationError,
} from '@adv-frontend/form-kit';
import { type OpenApiSchemaMediaItem, type OpenApiSchemaMediaItemV2 } from '../../../api/schema';
import { pluralize } from '../../string';
import { type ObjectField, type PrimitiveField } from '../types';
import { factoryGetErrorMessage } from './factoryGetErrorMessage';
import { createMediaMetadataField } from './mediaMetadata';

const MIN_COUNT_IMAGES_ERROR_CODE = 'MIN_COUNT_IMAGES_ERROR';

type MediaMinCountImagesParams = {
    minCountImages: number;
};

type FieldValidationError = ValidationError<typeof MIN_COUNT_IMAGES_ERROR_CODE, MediaMinCountImagesParams>;

export const createMediaField = (minCountImages: number = 0) =>
    createArrayField({
        field: createSingleMediaField(),
        validators: (createValidators) =>
            createValidators<FieldValidationError>({
                onChange: [
                    (value, fieldApi) => {
                        if (value.length < minCountImages) {
                            fieldApi.addValidationError({
                                code: MIN_COUNT_IMAGES_ERROR_CODE,
                                params: { minCountImages },
                            });
                        }
                    },
                ],
            }),
    });

const mediaFieldGetErrorMessage = factoryGetErrorMessage<FieldValidationError>({
    [MIN_COUNT_IMAGES_ERROR_CODE]: ({ minCountImages }) =>
        `Необходимо загрузить минимум ${pluralize(minCountImages, '%d файл', '%d файла', '%d файлов')}`,
});

export const getMediaFieldErrorMessage = (errors: FieldValidationError[]): string | undefined => {
    const error = errors[0];

    return error ? mediaFieldGetErrorMessage(error) : undefined;
};

export type SingleMediaFieldTypeV2 = {
    id: PrimitiveField<string>;
    display_sequence: PrimitiveField<number>;
    file_id: PrimitiveField<string>;
    file_path: PrimitiveField<string>;
    sizes: ArrayField<
        ObjectField<{
            alias: PrimitiveField<string>;
            width: PrimitiveField<number>;
        }>,
        never,
        never,
        never
    >;
    type: PrimitiveField<OpenApiSchemaMediaItemV2['type']>;
    video_id: PrimitiveField<string>;
    status: PrimitiveField<string>;
    first_frame_url: PrimitiveField<string>;
    stream_dash_url: PrimitiveField<string>;
    stream_hls_url: PrimitiveField<string>;
    title: PrimitiveField<string>;
    metadata: ReturnType<typeof createMediaMetadataField>;
};

export const createSingleMediaField = () =>
    createObjectField({
        fields: {
            id: createPrimitiveField({ defaultValue: '' }),
            display_sequence: createPrimitiveField({ defaultValue: 0 }),
            file_id: createPrimitiveField({ defaultValue: '' }),
            file_path: createPrimitiveField({ defaultValue: '' }),
            type: createPrimitiveField({
                defaultValue: literal<OpenApiSchemaMediaItem['type']>('IMAGE'),
            }),
            sizes: createArrayField({
                field: createObjectField({
                    fields: {
                        alias: createPrimitiveField({ defaultValue: '' }),
                        width: createPrimitiveField({ defaultValue: 0 }),
                    },
                }),
            }),
            metadata: createMediaMetadataField(),
        },
    });

export const createSingleMediaFieldV2 = () =>
    createObjectField({
        fields: {
            id: createPrimitiveField({ defaultValue: '' }),
            display_sequence: createPrimitiveField({ defaultValue: 0 }),
            file_id: createPrimitiveField({ defaultValue: '' }),
            file_path: createPrimitiveField({ defaultValue: '' }),
            type: createPrimitiveField({
                defaultValue: literal<OpenApiSchemaMediaItemV2['type']>('IMAGE'),
            }),
            sizes: createArrayField({
                field: createObjectField({
                    fields: {
                        alias: createPrimitiveField({ defaultValue: '' }),
                        width: createPrimitiveField({ defaultValue: 0 }),
                    },
                }),
            }),
            // Добавляем поля для видео
            video_id: createPrimitiveField({ defaultValue: '' }),
            status: createPrimitiveField({ defaultValue: '' }),
            first_frame_url: createPrimitiveField({ defaultValue: '' }),
            stream_dash_url: createPrimitiveField({ defaultValue: '' }),
            stream_hls_url: createPrimitiveField({ defaultValue: '' }),
            title: createPrimitiveField({ defaultValue: '' }),
            metadata: createMediaMetadataField(),
        },
    });

export const MEDIA_NOT_SELECTED = 'notSelected' as const;

export const nullableMediaField = createUnionField({
    key: 'type',
    defaultValue: { type: MEDIA_NOT_SELECTED },
    fields: [
        createObjectField({
            fields: {
                type: createPrimitiveField({
                    defaultValue: literal<MediaNotSelected>(MEDIA_NOT_SELECTED),
                }),
            },
        }),
        createSingleMediaField(),
    ],
});

export type NullableMediaField = InferFieldValue<typeof nullableMediaField>;

export const mediaField = createMediaField(1);
export type MediaField = InferFieldValue<(typeof mediaField)['field']>;

export type MediaNotSelected = typeof MEDIA_NOT_SELECTED;
